/**
 * Renders an optionally-modifiable table row representing the given model, displaying the given fields.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * model | The model associated with the row.
 * inputViews | A mapping from field `id` to the `InputView` representing the field.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * commit | None. | Fired whenever the save control is activated by the user.
 * remove | None. | Fired whenever the delete control is activated by the user.
 * @module app/views/ModelRowView
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import InputView from "core/views/InputView";
export default Backbone.View.extend({
  tagName: "tr",

  model: {},
  inputViews: {},

  events: {
    "click .save-row": function () {
      this.trigger("commit");
    },
    "click .delete-row": function () {
      this.trigger("remove");
    },
    mouseenter: function () {
      this.$(".edit select").on(
        "focus",
        _.bind(function (evt) {
          this._skipLeave = true;
          $(evt.target).one(
            "change, blur",
            _.bind(function () {
              this._skipLeave = false;
            }, this)
          );
        }, this)
      );

      if (this.options.mode != "view") {
        this._showEdit();
      }
    },
    mouseleave: function () {
      if (!this._skipLeave) {
        if (this.options.autoSave) {
          if (this.options.mode != "view") {
            this._showEdit();
          }
        } else {
          this.edit(this._edited);
        }
      }
    },
    touchstart: function () {
      this.$(".edit select").on(
        "focus",
        _.bind(function (evt) {
          this._skipLeave = true;
          $(evt.target).one(
            "change, blur",
            _.bind(function () {
              this._skipLeave = false;
            }, this)
          );
        }, this)
      );

      if (this.options.mode != "view") {
        this._showEdit();
      }
    },
  },

  template: _.template(
    '<%if(allowSave){%><td class="option-container action-view"><span class="save-container edited"><a class="save-row" href="#"><i class="glyphicon glyphicon-ok"></i></a></span></td><%}%><%if(allowDelete){%><td class="option-container action-view"><span class="delete-container edit"><a href="#" class="delete-row close">&times;</a></span></td><%}%>'
  ),

  /**
   * Initializes the control.
   * @param  {object} options The options hash.
   * @param {Boolean} options.allowSave:true True if saving (and modification of) the model should be allowed.
   * @param {Boolean} options.allowDelete:true True if deleting of the model should be allowed.
   * @param {Boolean} options.autoSave:false True to persist model changes automatically, otherwise false.
   * @param {String} [options.mode] Specifies the display mode for the row. This should be "edit" or "view". If not provided, the row will enter "edit" mode upon cursor entry.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      allowSave: true,
      allowDelete: true,
      autoSave: false,
    });
    this.model = this.options.model;
    this.inputViews = {};
  },

  render: function () {
    this.$el.html(this.template(this.options));

    var firstOption;

    if (this.options.allowSave || this.options.allowDelete) {
      firstOption = this.$(".option-container").first();
    }

    $.each(
      this.options.fields,
      _.bind(function (index) {
        this._addField(this.options.fields[index], firstOption);
      }, this)
    );

    if (this.options.autoSave || this.options.mode == "edit") {
      this.$(".edited").show();
      this._showEdit();
    } else {
      this.$(".edited").hide();
      this._hideEdit();
    }
  },

  /**
   * Adds a field to the row. This function shall only be called *after* `render` has executed.
   * @param {Field} field The field to add.
   */
  addField: function (field) {
    var firstOption;

    if (this.options.allowSave || this.options.allowDelete) {
      firstOption = this.$(".option-container").first();
    }

    this.options.fields.push(field);
    this._addField(field, firstOption);
  },

  /**
   * Removes a field from the row.
   * @param  {string} attribute The id of the field to remove.
   */
  removeField: function (attribute) {
    var removeIndex = false;

    _.each(this.options.fields, function (field, idx) {
      if (field.attributes.name == attribute) {
        removeIndex = idx;
      }
    });

    if (removeIndex !== false) {
      this.options.fields.splice(removeIndex, 1);
      this.inputViews[attribute].$el.parent().remove();
    }
  },

  _addField: function (field, firstOption) {
    var view = this._createInputView(this.model, field);

    view.render();
    view.on(
      "change",
      function () {
        if (this.options.autoSave) {
          this.trigger("commit");
        } else {
          this.edit();
        }
      },
      this
    );
    var cell = $('<td class="field-cell"></td>');

    cell.addClass(field.attributes.className);
    cell.append(view.$el);

    if (firstOption) {
      firstOption.before(cell);
    } else {
      this.$el.append(cell);
    }
  },

  remove: function () {
    this.$el.remove();
  },

  _edited: false,

  /**
   * Programmatically set the edited state of the row. By default, the save and edit controls are hidden. This function can be called to override that.
   * @param  {Boolean} [value:true] True to show edit controls, otherwise false.
   */
  edit: function (value) {
    if (value === false) {
      if (this.options.mode != "edit") {
        this.$(".edited").hide();
        this._hideEdit();
      }
      this._edited = false;
    } else {
      if (this.options.mode != "view") {
        this.$(".edited").show();
        this._showEdit();
      }
      this._edited = true;
    }
  },

  _showEdit: function () {
    _.each(
      this.inputViews,
      function (inputView) {
        inputView.showEdit();
      },
      this
    );
  },

  _hideEdit: function () {
    _.each(
      this.inputViews,
      function (inputView) {
        inputView.hideEdit();
      },
      this
    );
  },

  _createInputView: function (model, field) {
    var itemView = new InputView({
      model: model,
      field: field,
      mode: this.options.mode,
    });

    this.inputViews[field.get("name")] = itemView;

    return itemView;
  },

  delegateEvents: function () {
    Backbone.View.prototype.delegateEvents.apply(this, arguments);
    _.each(this.inputViews, function (inputView) {
      inputView.delegateEvents();
    });
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/ModelRowView.js
// module id = 835
// module chunks = 1